'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { trackEvent } from '@/lib/analytics/googleAnalytics';
import Script from 'next/script';
import Image from 'next/image';
import BookDemoModal from '@/components/common/BookDemoModal';
import CrispChat from '@/components/CrispChat';

export default function LandingPage() {
  const { user } = useAuth();
  const router = useRouter();
  const [scrollDepth, setScrollDepth] = useState(0);
  const [timeOnPage, setTimeOnPage] = useState(0);
  const pageLoadTime = useRef<number>(Date.now());
  const scrollDepthTracked = useRef<Set<number>>(new Set());
  const [isAnnual, setIsAnnual] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [revenue, setRevenue] = useState(50);
  const [aiSystems, setAiSystems] = useState(5);
  const [revenueDisplay, setRevenueDisplay] = useState(50);
  const [aiSystemsDisplay, setAiSystemsDisplay] = useState(5);
  const [activeSlider, setActiveSlider] = useState<string | null>(null);

  const pageUrl = 'https://chronomind.ai';
  const logoUrl = `${pageUrl}/assets/images/Untitled_design-28-1772205376571.png`;
  const ogImageUrl = logoUrl;

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${pageUrl}/#organization`,
        name: 'ChronoMind™',
        url: pageUrl,
        logo: {
          '@type': 'ImageObject',
          url: logoUrl,
          width: 512,
          height: 512,
        },
        description: 'Enterprise AI risk management platform with ChronoMind™ Risk Index™, governance maturity modeling, ISO 42001/SOC 2 certification tracking, and predictive risk analytics',
        sameAs: [],
      },
      {
        '@type': 'SoftwareApplication',
        '@id': `${pageUrl}/#softwareapplication`,
        name: 'ChronoMind™',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        description: 'Enterprise AI risk management platform with ChronoMind™ Risk Index™, governance maturity modeling, ISO 42001/SOC 2 certification tracking, and predictive risk analytics',
        url: pageUrl,
        offers: [
          { '@type': 'Offer', name: 'Basic Plan', price: '0', priceCurrency: 'USD', description: 'Up to 5 AI use cases, 3 team members, basic risk assessments' },
          { '@type': 'Offer', name: 'Professional Plan', price: '299', priceCurrency: 'USD', description: 'Up to 50 AI use cases, 15 team members, advanced analytics, certification tracking' },
          { '@type': 'Offer', name: 'Enterprise Plan', price: '799', priceCurrency: 'USD', description: 'Unlimited AI use cases, unlimited team members, white-label options, dedicated support' },
        ],
        aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', ratingCount: '127', bestRating: '5', worstRating: '1' },
        author: { '@type': 'Organization', name: 'ChronoMind™' },
      },
      {
        '@type': 'WebSite',
        '@id': `${pageUrl}/#website`,
        url: pageUrl,
        name: 'ChronoMind™',
        description: 'Executive AI Governance Command Center',
        publisher: { '@id': `${pageUrl}/#organization` },
      },
    ],
  };

  useEffect(() => {
    trackEvent('page_view', { page_path: '/', page_title: 'ChronoMind™ - AI Governance Platform' });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollPercentage = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);
      setScrollDepth(scrollPercentage);
      [25, 50, 75, 100].forEach((milestone) => {
        if (scrollPercentage >= milestone && !scrollDepthTracked.current.has(milestone)) {
          scrollDepthTracked.current.add(milestone);
          trackEvent('page_view', { scroll_depth: milestone, page_path: '/' });
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = Math.floor((Date.now() - pageLoadTime.current) / 1000);
      setTimeOnPage(currentTime);
      if ([30, 60, 120].includes(currentTime)) {
        trackEvent('page_view', { time_on_landing_page: currentTime, page_path: '/' });
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const observerOptions = { threshold: 0.08, rootMargin: '0px 0px -40px 0px' };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Animated counter effect for ROI sliders
  useEffect(() => {
    const timer = setTimeout(() => setRevenueDisplay(revenue), 50);
    return () => clearTimeout(timer);
  }, [revenue]);

  useEffect(() => {
    const timer = setTimeout(() => setAiSystemsDisplay(aiSystems), 50);
    return () => clearTimeout(timer);
  }, [aiSystems]);

  const handleCTAClick = (location: string) => {
    trackEvent('page_view', { cta_location: location, page_path: '/' });
  };

  const handleFeatureClick = (featureName: string) => {
    trackEvent('page_view', { feature_name: featureName, page_path: '/' });
  };

  const handlePricingClick = (tier: string) => {
    trackEvent('page_view', { pricing_tier: tier, page_path: '/' });
  };

  const handleNavigationClick = (menuItem: string) => {
    trackEvent('page_view', { navigation_item: menuItem, page_path: '/' });
  };

  const frameworkLayers = [
    {
      number: '01',
      title: 'Governance Layer',
      subtitle: 'Policy Oversight & Compliance Management',
      description: 'Establish enterprise-wide AI governance policies with automated compliance tracking, regulatory alignment, and board-level reporting across ISO 42001, SOC 2, GDPR, and EU AI Act frameworks.',
      icon: 'DocumentTextIcon',
      features: ['Policy document management', 'Regulatory compliance mapping', 'Board-level reporting', 'Audit trail automation'],
      color: 'from-blue-600/20 to-blue-800/10',
      borderColor: 'border-blue-500/30',
      iconBg: 'bg-blue-500/20',
      iconColor: 'text-blue-400',
      delay: '0.1s',
    },
    {
      number: '02',
      title: 'Risk & Ethics Layer',
      subtitle: 'Risk Assessment & Ethical AI',
      description: 'Comprehensive risk evaluation with multi-dimensional impact scoring, ethical AI assessment frameworks, and automated mitigation strategy recommendations for responsible AI deployment.',
      icon: 'ShieldCheckIcon',
      features: ['CMRI risk scoring (0-100)', 'Ethical impact assessment', 'Automated mitigation strategies', 'Real-time risk monitoring'],
      color: 'from-red-600/20 to-red-800/10',
      borderColor: 'border-red-500/30',
      iconBg: 'bg-red-500/20',
      iconColor: 'text-red-400',
      delay: '0.2s',
    },
    {
      number: '03',
      title: 'Performance Integration Layer',
      subtitle: 'KPI Tracking & ROI Measurement',
      description: 'Connect AI governance to business outcomes with integrated KPI dashboards, ROI measurement frameworks, and predictive performance analytics that demonstrate governance value to stakeholders.',
      icon: 'ChartBarIcon',
      features: ['KPI dashboard integration', 'ROI measurement tools', 'Predictive trend modeling', 'Executive narrative generation'],
      color: 'from-green-600/20 to-green-800/10',
      borderColor: 'border-green-500/30',
      iconBg: 'bg-green-500/20',
      iconColor: 'text-green-400',
      delay: '0.3s',
    },
    {
      number: '04',
      title: 'Culture Alignment Layer',
      subtitle: 'Organizational Adoption & Change Management',
      description: 'Drive enterprise-wide AI governance adoption with organizational readiness assessments, change management workflows, team training tracking, and culture maturity modeling across departments.',
      icon: 'UserGroupIcon',
      features: ['Organizational readiness scoring', 'Change management workflows', 'Department-level analytics', 'Governance maturity modeling'],
      color: 'from-purple-600/20 to-purple-800/10',
      borderColor: 'border-purple-500/30',
      iconBg: 'bg-purple-500/20',
      iconColor: 'text-purple-400',
      delay: '0.4s',
    },
  ];

  const certificationTiers = [
    {
      level: 'Level I',
      badge: 'Foundation',
      tier: 'Basic Tier',
      description: 'Establish foundational AI governance practices with core risk assessment capabilities and basic compliance tracking.',
      features: ['Core risk assessments', 'Basic CMRI scoring', 'Policy documentation', 'Team collaboration (3 members)'],
      price: 'Free',
      color: 'from-slate-700 to-slate-800',
      borderColor: 'border-slate-500/50',
      badgeColor: 'text-slate-300',
      roman: 'I',
      delay: '0.1s',
    },
    {
      level: 'Level II',
      badge: 'Advanced',
      tier: 'Professional Tier',
      description: 'Advanced governance capabilities with comprehensive certification tracking, executive reporting, and department-level analytics.',
      features: ['Unlimited use cases', 'ISO 42001 & GDPR tracking', 'Executive board reports', 'Advanced analytics (10 members)'],
      price: '$299/mo',
      color: 'from-[#1E3A5F] to-[#0F2A4A]',
      borderColor: 'border-[#C9A961]/50',
      badgeColor: 'text-[#C9A961]',
      roman: 'II',
      featured: true,
      delay: '0.2s',
    },
    {
      level: 'Level III',
      badge: 'Strategic',
      tier: 'Enterprise Tier — SIA',
      description: 'Strategic Intelligence Architecture — the pinnacle of AI governance with predictive modeling, full certification suite, and dedicated enterprise support.',
      features: ['Predictive risk modeling', 'Full certification suite (ISO 42001, SOC 2, EU AI Act)', 'Governance maturity modeling', 'Unlimited team + dedicated support'],
      price: '$799/mo',
      color: 'from-[#1a1a2e] to-[#16213e]',
      borderColor: 'border-purple-500/40',
      badgeColor: 'text-purple-300',
      roman: 'III',
      delay: '0.3s',
    },
  ];

  // ROI Calculator
  const potentialFine = Math.round(revenueDisplay * 0.06);
  const platformCost = isAnnual ? Math.round(799 * 12 * 0.8) : 799 * 12;
  const savings = Math.max(0, potentialFine * 1000000 - platformCost);

  return (
    <>
      <Script
        id="structured-data"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <CrispChat welcomeMessage="👋 Want a demo? Chat with us!" />

      <style jsx global>{`
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.7s ease-out, transform 0.7s ease-out;
        }
        .animate-on-scroll.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        .animate-on-scroll.scale-start {
          transform: scale(0.95) translateY(0);
        }
        .animate-on-scroll.scale-start.animate-in {
          transform: scale(1) translateY(0);
        }
        .gold-glow {
          text-shadow: 0 0 30px rgba(201, 169, 97, 0.4), 0 2px 8px rgba(201, 169, 97, 0.3);
        }
        .card-hover {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .card-hover:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
        }
        .section-divider {
          background: linear-gradient(to right, transparent, rgba(201, 169, 97, 0.3), transparent);
          height: 1px;
        }
        .particle-overlay {
          background: linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(0, 0, 0, 0.7) 100%);
        }
        /* Industry icon hover animation */
        .industry-icon-wrap {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .industry-icon-wrap:hover {
          transform: scale(1.1);
          box-shadow: 0 0 20px rgba(201, 169, 97, 0.4);
        }
        /* ROI Slider styles */
        .roi-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 6px;
          border-radius: 3px;
          background: rgba(255,255,255,0.15);
          outline: none;
          cursor: pointer;
        }
        .roi-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #C9A961;
          border: 2px solid #C9A961;
          cursor: pointer;
          transition: box-shadow 0.2s ease, transform 0.2s ease;
        }
        .roi-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #C9A961;
          border: 2px solid #C9A961;
          cursor: pointer;
          transition: box-shadow 0.2s ease;
        }
        .roi-slider:focus::-webkit-slider-thumb,
        .roi-slider:hover::-webkit-slider-thumb {
          box-shadow: 0 0 0 4px rgba(201, 169, 97, 0.3);
          transform: scale(1.1);
        }
        .roi-slider:focus::-moz-range-thumb,
        .roi-slider:hover::-moz-range-thumb {
          box-shadow: 0 0 0 4px rgba(201, 169, 97, 0.3);
        }
        .roi-slider.active-slider::-webkit-slider-thumb {
          box-shadow: 0 0 0 6px rgba(201, 169, 97, 0.25), 0 0 12px rgba(201, 169, 97, 0.4);
        }
        .roi-value-display {
          transition: all 0.15s ease;
        }
        /* Crisp widget position override */
        #crisp-chatbox {
          bottom: 40px !important;
        }
        #crisp-chatbox > div {
          transform: scale(0.9);
          transform-origin: bottom right;
        }
      `}</style>

      <div className="min-h-screen" style={{ backgroundColor: '#0F172A', color: '#ffffff' }}>

        {/* ===== NAVIGATION ===== */}
        {/* Updated nav order: Product → Framework → Features → Pricing → Certifications → Login → Start Free Trial */}
        <nav className="fixed top-0 left-0 right-0 z-[1000] backdrop-blur-md border-b" style={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', borderColor: 'rgba(201, 169, 97, 0.2)' }}>
          <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="flex h-20 items-center justify-between">
              <AppLogo
                src="/assets/images/Untitled_design-28-1772205376571.png"
                size={86}
                className="cursor-pointer"
              />
              <div className="hidden items-center space-x-6 lg:space-x-8 md:flex">
                <a href="#product" className="font-body text-sm font-medium transition-colors hover:text-yellow-400" style={{ color: 'rgba(255,255,255,0.8)' }} onClick={() => handleNavigationClick('product')}>Product</a>
                <a href="#framework" className="font-body text-sm font-medium transition-colors hover:text-yellow-400" style={{ color: 'rgba(255,255,255,0.8)' }} onClick={() => handleNavigationClick('framework')}>Framework</a>
                <a href="#features" className="font-body text-sm font-medium transition-colors hover:text-yellow-400" style={{ color: 'rgba(255,255,255,0.8)' }} onClick={() => handleNavigationClick('features')}>Features</a>
                <a href="#pricing" className="font-body text-sm font-medium transition-colors hover:text-yellow-400" style={{ color: 'rgba(255,255,255,0.8)' }} onClick={() => handleNavigationClick('pricing')}>Pricing</a>
                <a href="#certifications" className="font-body text-sm font-medium transition-colors hover:text-yellow-400" style={{ color: 'rgba(255,255,255,0.8)' }} onClick={() => handleNavigationClick('certifications')}>Certifications</a>
                <span className="text-white/20">|</span>
                <Link href="/login" className="font-body text-sm font-medium transition-colors hover:text-yellow-400" style={{ color: 'rgba(255,255,255,0.8)' }} onClick={() => handleNavigationClick('login')}>Login</Link>
                <Link
                  href="/login?mode=signup"
                  className="rounded-md px-5 py-2.5 min-h-[44px] flex items-center font-body text-sm font-semibold shadow-md transition-all duration-300 hover:scale-105"
                  style={{ backgroundColor: '#C9A961', color: '#0F172A' }}
                  onClick={() => handleCTAClick('nav_signup')}
                >
                  Start Free Trial
                </Link>
              </div>
              <div className="flex items-center space-x-3 md:hidden">
                <Link href="/login" className="font-body text-sm font-medium min-h-[44px] flex items-center" style={{ color: 'rgba(255,255,255,0.8)' }}>Login</Link>
                <Link href="/login?mode=signup" className="rounded-md px-3 py-2 min-h-[44px] flex items-center font-body text-sm font-semibold" style={{ backgroundColor: '#C9A961', color: '#0F172A' }}>Start Free Trial</Link>
              </div>
            </div>
          </div>
        </nav>

        {/* ===== HERO SECTION ===== */}
        <section id="product" className="relative overflow-hidden pt-20" style={{ minHeight: '100vh' }}>
          {/* Particle Background */}
          <div className="absolute inset-0 z-0">
            <Image
              src="/assets/images/ChatGPT_Image_Feb_27_2026_at_09_29_58_AM-1772204341453.png"
              alt="Dark particle texture background with gold light particles for premium enterprise aesthetic"
              fill
              className="object-cover"
              priority
              quality={90}
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,23,42,0.88) 0%, rgba(0,0,0,0.75) 50%, rgba(15,23,42,0.88) 100%)' }} />
          </div>

          {/* Bottom gradient transition */}
          <div className="absolute bottom-0 left-0 right-0 h-32 z-10 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, #0F172A)' }} />

          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-12 py-20 lg:py-28">
            {/* Left: Text Content */}
            <div className="flex-1 max-w-2xl text-center lg:text-left">
              {/* Hero badge with reduced opacity (10%) and blur */}
              <div
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-6 border"
                style={{
                  backgroundColor: 'rgba(201,169,97,0.1)',
                  borderColor: 'rgba(201,169,97,0.3)',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#C9A961' }} />
                <span className="font-body text-xs font-semibold tracking-widest uppercase" style={{ color: '#C9A961' }}>Enterprise AI Governance</span>
              </div>

              {/* h1 with improved typography: tracking-tight, leading-tight (1.1) */}
              <h1
                className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white"
                style={{ letterSpacing: '-0.025em', lineHeight: '1.1' }}
              >
                Executive AI Governance
                <br />
                <span className="gold-glow font-extrabold" style={{ color: '#C9A961' }}>Command Center</span>
              </h1>

              <p className="mt-6 font-body text-base sm:text-lg lg:text-xl leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)', marginBottom: '1.5rem' }}>
                Enterprise-grade AI risk management platform for Fortune 500 organizations and Federal Agencies.
                Comprehensive governance, compliance tracking, and predictive risk analytics aligned with
                ISO 42001, SOC 2, GDPR, and EU AI Act requirements.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  href="/login?mode=signup"
                  className="rounded-lg py-4 px-8 min-h-14 flex items-center justify-center font-body text-base font-semibold shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
                  style={{ backgroundColor: '#C9A961', color: '#0F172A', boxShadow: '0 4px 20px rgba(201,169,97,0.45)' }}
                  onClick={() => handleCTAClick('hero_cta')}
                >
                  Start Free Trial
                </Link>
                <button
                  onClick={() => setShowDemoModal(true)}
                  className="rounded-lg border-2 py-4 px-8 min-h-14 flex items-center justify-center font-body text-base font-semibold transform transition-all duration-300 hover:scale-105"
                  style={{ borderColor: 'rgba(201,169,97,0.6)', color: '#C9A961', backgroundColor: 'rgba(201,169,97,0.05)' }}
                >
                  Book a Demo
                </button>
              </div>

              {/* Updated claim text */}
              <p className="mt-6 font-body text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                Built for Federal Agencies and Enterprise Organizations • No credit card required
              </p>

              {/* Trust Metrics */}
              <div className="mt-10 flex flex-wrap gap-6 justify-center lg:justify-start">
                {[{ value: 'ISO 42001', label: 'Certified' }, { value: 'SOC 2', label: 'Compliant' }, { value: 'GDPR', label: 'Ready' }, { value: 'EU AI Act', label: 'Aligned' }].map((metric) => (
                  <div key={metric.value} className="text-center">
                    <div className="font-heading text-lg font-bold" style={{ color: '#C9A961' }}>{metric.value}</div>
                    <div className="font-body text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{metric.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: 3D Framework Graphic */}
            <div className="flex-1 max-w-xl w-full flex items-center justify-center animate-on-scroll" style={{ transitionDelay: '0.3s' }}>
              <div className="relative w-full">
                <div className="absolute inset-0 rounded-2xl blur-3xl opacity-30" style={{ background: 'radial-gradient(ellipse, rgba(201,169,97,0.4) 0%, transparent 70%)' }} />
                <Image
                  src="/assets/images/ChatGPT_Image_Feb_27_2026_at_09_29_58_AM-1772204341453.png"
                  alt="3D layered framework stack visualization showing four governance layers: Governance Layer with compliance dashboards, Risk and Ethics Layer with heat maps, Performance Integration Layer with KPI charts, and Culture Alignment Layer with organizational metrics"
                  width={600}
                  height={500}
                  className="relative z-10 w-full h-auto rounded-xl"
                  style={{ filter: 'drop-shadow(0 20px 60px rgba(201,169,97,0.25))' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ===== FRAMEWORK EXPLANATION SECTION ===== */}
        <section id="framework" className="relative py-20 lg:py-28 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(180deg, #0F172A 0%, #0a1628 50%, #0F172A 100%)', paddingTop: '80px', paddingBottom: '80px' }}>
          <div className="section-divider mb-16" />
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-16 animate-on-scroll">
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4 border" style={{ backgroundColor: 'rgba(201,169,97,0.1)', borderColor: 'rgba(201,169,97,0.3)' }}>
                <span className="font-body text-xs font-semibold tracking-widest uppercase" style={{ color: '#C9A961' }}>4-Layer Architecture</span>
              </div>
              {/* h2 with improved typography */}
              <h2
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
                style={{ letterSpacing: '-0.025em', marginBottom: '1.5rem' }}
              >
                The ChronoMind™ Governance Framework
              </h2>
              <p className="mx-auto mt-5 max-w-2xl font-body text-base lg:text-lg" style={{ color: 'rgba(255,255,255,0.65)' }}>
                A comprehensive four-layer architecture designed to address every dimension of enterprise AI governance
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {frameworkLayers.map((layer) => (
                <div
                  key={layer.number}
                  className="card-hover rounded-2xl border p-7 lg:p-8 animate-on-scroll"
                  style={{
                    background: `linear-gradient(135deg, rgba(30,58,95,0.4) 0%, rgba(15,23,42,0.8) 100%)`,
                    borderColor: 'rgba(201,169,97,0.15)',
                    transitionDelay: layer.delay,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <div className="flex items-start gap-5">
                    <div className="flex-shrink-0">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${layer.iconBg}`}>
                        <Icon name={layer.icon as any} size={28} className={layer.iconColor} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-heading text-xs font-bold tracking-widest" style={{ color: '#C9A961' }}>{layer.number}</span>
                        <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>|</span>
                        <span className="font-body text-xs font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>{layer.subtitle}</span>
                      </div>
                      <h3 className="font-heading text-xl font-bold text-white mb-3">{layer.title}</h3>
                      <p className="font-body text-sm leading-relaxed mb-5" style={{ color: 'rgba(255,255,255,0.65)' }}>{layer.description}</p>
                      <ul className="space-y-2">
                        {layer.features.map((feature) => (
                          <li key={feature} className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#C9A961' }} />
                            <span className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="section-divider mt-16" />
        </section>

        {/* ===== EXECUTIVE PHOTOGRAPHY SECTION ===== */}
        <section className="relative overflow-hidden" style={{ minHeight: '500px' }}>
          <div className="absolute inset-0 z-0">
            <Image
              src="/assets/images/ChatGPT_Image_Feb_27_2026_at_09_29_58_AM-1772204341453.png"
              alt="Diverse executive team of three professionals in a modern boardroom reviewing AI governance dashboards on large screens, demonstrating enterprise-level strategic decision making"
              fill
              className="object-cover"
              quality={85}
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,23,42,0.92) 0%, rgba(30,58,95,0.85) 40%, rgba(15,23,42,0.92) 100%)' }} />
          </div>
          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
            <div className="max-w-3xl mx-auto text-center animate-on-scroll">
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-6 border" style={{ backgroundColor: 'rgba(201,169,97,0.15)', borderColor: 'rgba(201,169,97,0.4)' }}>
                <Icon name="ShieldCheckIcon" size={14} className="text-yellow-400" />
                <span className="font-body text-xs font-semibold tracking-widest uppercase" style={{ color: '#C9A961' }}>Enterprise Trust</span>
              </div>
              {/* Updated claim text */}
              <h2
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight"
                style={{ letterSpacing: '-0.025em', marginBottom: '1.5rem' }}
              >
                Built for Federal Agencies and
                <br />
                <span className="gold-glow" style={{ color: '#C9A961' }}>Enterprise Organizations</span>
              </h2>
              <p className="mt-6 font-body text-base lg:text-lg" style={{ color: 'rgba(255,255,255,0.75)' }}>
                ChronoMind™ powers AI governance for the world's most demanding organizations —
                from DoD agencies to global financial institutions — where compliance, accountability,
                and risk management are mission-critical.
              </p>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
                {[
                  { value: '500+', label: 'Enterprise Clients' },
                  { value: '99.9%', label: 'Uptime SLA' },
                  { value: '4.8/5', label: 'Client Rating' },
                  { value: 'FedRAMP', label: 'Ready' },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-xl p-4 border" style={{ backgroundColor: 'rgba(15,23,42,0.6)', borderColor: 'rgba(201,169,97,0.2)', backdropFilter: 'blur(10px)' }}>
                    <div className="font-heading text-2xl font-bold" style={{ color: '#C9A961' }}>{stat.value}</div>
                    <div className="font-body text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== FEATURES SECTION ===== */}
        <section id="features" className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(180deg, #0F172A 0%, #0a1628 100%)', paddingTop: '80px', paddingBottom: '80px' }}>
          <div className="section-divider mb-16" />
          <div className="mx-auto max-w-7xl">
            <div className="mb-16 text-center animate-on-scroll">
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4 border" style={{ backgroundColor: 'rgba(201,169,97,0.1)', borderColor: 'rgba(201,169,97,0.3)' }}>
                <span className="font-body text-xs font-semibold tracking-widest uppercase" style={{ color: '#C9A961' }}>Platform Capabilities</span>
              </div>
              <h2
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
                style={{ letterSpacing: '-0.025em', marginBottom: '1.5rem' }}
              >
                Enterprise AI Governance Platform
              </h2>
              <p className="mx-auto mt-5 max-w-2xl font-body text-base lg:text-lg" style={{ color: 'rgba(255,255,255,0.65)' }}>
                Comprehensive risk management and compliance tracking for AI systems
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {[
                { icon: 'ChartBarIcon', title: 'ChronoMind™ Risk Index™ (CMRI)', desc: 'Proprietary risk scoring methodology combining likelihood, impact, and compliance factors into a unified executive metric (0-100 scale).', delay: '0.1s' },
                { icon: 'AcademicCapIcon', title: 'Governance Maturity Model', desc: 'Five-stage maturity assessment (Ad Hoc → Optimized) aligned with NIST AI RMF and ISO 42001 governance frameworks.', delay: '0.2s' },
                { icon: 'LightBulbIcon', title: 'Predictive Risk Analytics', desc: 'AI-powered trend forecasting and risk trajectory modeling for proactive governance decision-making.', delay: '0.3s' },
                { icon: 'ShieldCheckIcon', title: 'ISO 42001/SOC 2/GDPR Tracking', desc: 'Automated compliance monitoring with evidence management, audit trails, and certification readiness dashboards.', delay: '0.15s' },
                { icon: 'UserGroupIcon', title: 'Multi-tenant RBAC', desc: 'Enterprise-grade role-based access control with organization isolation, audit logging, and granular permission management.', delay: '0.25s' },
                { icon: 'BellIcon', title: 'Real-time Collaboration', desc: 'Instant notifications, approval workflows, and team coordination for distributed governance teams.', delay: '0.35s' },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className="card-hover rounded-xl border p-7 animate-on-scroll"
                  style={{
                    background: 'linear-gradient(135deg, rgba(30,58,95,0.35) 0%, rgba(15,23,42,0.7) 100%)',
                    borderColor: 'rgba(201,169,97,0.15)',
                    transitionDelay: feature.delay,
                    backdropFilter: 'blur(8px)',
                  }}
                  onClick={() => handleFeatureClick(feature.title)}
                >
                  {/* Larger 48px icon with hover animation */}
                  <div className="mb-4 industry-icon-wrap inline-flex h-12 w-12 items-center justify-center rounded-lg" style={{ backgroundColor: 'rgba(201,169,97,0.15)' }}>
                    <Icon name={feature.icon as any} size={28} style={{ color: '#C9A961' }} />
                  </div>
                  <h3 className="mb-3 font-heading text-lg font-bold text-white">{feature.title}</h3>
                  <p className="font-body text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="section-divider mt-16" />
        </section>

        {/* ===== CERTIFICATION PATHWAY SECTION ===== */}
        {/* Labeled as "Certification Membership Plans" */}
        <section id="certifications" className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #0F172A 100%)', paddingTop: '80px', paddingBottom: '80px' }}>
          <div className="mx-auto max-w-7xl">
            <div className="text-center mb-16 animate-on-scroll">
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4 border" style={{ backgroundColor: 'rgba(201,169,97,0.1)', borderColor: 'rgba(201,169,97,0.3)' }}>
                <span className="font-body text-xs font-semibold tracking-widest uppercase" style={{ color: '#C9A961' }}>Certification Membership</span>
              </div>
              <h2
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
                style={{ letterSpacing: '-0.025em', marginBottom: '1.5rem' }}
              >
                Certification Membership Plans
              </h2>
              <p className="mx-auto mt-5 max-w-2xl font-body text-base lg:text-lg" style={{ color: 'rgba(255,255,255,0.65)' }}>
                For AI governance practitioners — three progressive certification tiers designed to grow with your governance maturity
              </p>
            </div>

            {/* Pathway connector */}
            <div className="hidden lg:flex items-center justify-center mb-8">
              <div className="flex items-center gap-0 w-full max-w-3xl">
                <div className="flex-1 h-0.5" style={{ background: 'linear-gradient(to right, transparent, rgba(201,169,97,0.5))' }} />
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#C9A961' }} />
                <div className="flex-1 h-0.5" style={{ backgroundColor: 'rgba(201,169,97,0.5)' }} />
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#C9A961' }} />
                <div className="flex-1 h-0.5" style={{ background: 'linear-gradient(to right, rgba(201,169,97,0.5), transparent)' }} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              {certificationTiers.map((tier) => (
                <div
                  key={tier.level}
                  className={`card-hover rounded-2xl border p-8 animate-on-scroll relative overflow-hidden ${tier.featured ? 'ring-2' : ''}`}
                  style={{
                    background: `linear-gradient(135deg, rgba(30,58,95,0.5) 0%, rgba(15,23,42,0.9) 100%)`,
                    borderColor: tier.featured ? '#C9A961' : 'rgba(201,169,97,0.2)',
                    ringColor: tier.featured ? '#C9A961' : 'transparent',
                    transitionDelay: tier.delay,
                  }}
                >
                  {tier.featured && (
                    <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(to right, transparent, #C9A961, transparent)' }} />
                  )}

                  {/* Badge */}
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div
                        className="w-24 h-24 rounded-2xl flex flex-col items-center justify-center border-2"
                        style={{
                          background: tier.featured
                            ? 'linear-gradient(135deg, rgba(201,169,97,0.3) 0%, rgba(201,169,97,0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
                          borderColor: tier.featured ? '#C9A961' : 'rgba(255,255,255,0.15)',
                          boxShadow: tier.featured ? '0 0 30px rgba(201,169,97,0.3)' : 'none',
                        }}
                      >
                        <span className="font-heading text-3xl font-bold" style={{ color: tier.featured ? '#C9A961' : 'rgba(255,255,255,0.7)' }}>{tier.roman}</span>
                        {tier.level === 'Level III' && (
                          <span className="font-body text-xs font-bold tracking-widest" style={{ color: 'rgba(255,255,255,0.5)' }}>SIA</span>
                        )}
                      </div>
                      {tier.featured && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#C9A961' }}>
                          <Icon name="StarIcon" size={12} style={{ color: '#0F172A' }} />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="text-center mb-6">
                    <div className="font-body text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: tier.badgeColor }}>{tier.level} — {tier.badge}</div>
                    <h3 className="font-heading text-xl font-bold text-white">{tier.tier}</h3>
                    <div className="mt-3">
                      <span className="font-heading text-3xl font-bold" style={{ color: tier.featured ? '#C9A961' : 'white' }}>{tier.price}</span>
                    </div>
                  </div>

                  <p className="font-body text-sm leading-relaxed mb-6 text-center" style={{ color: 'rgba(255,255,255,0.6)' }}>{tier.description}</p>

                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Icon name="CheckCircleIcon" size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#C9A961' }} />
                        <span className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    href="/login?mode=signup"
                    className="block w-full rounded-lg py-3 text-center font-body text-sm font-bold transition-all hover:scale-105"
                    style={{
                      backgroundColor: tier.featured ? '#C9A961' : 'transparent',
                      color: tier.featured ? '#0F172A' : '#C9A961',
                      border: tier.featured ? 'none' : '2px solid rgba(201,169,97,0.5)',
                    }}
                    onClick={() => handlePricingClick(tier.level)}
                  >
                    Start Free Trial
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Visual divider between Certification Membership and Enterprise Platform Pricing */}
        <div className="py-6 px-4 sm:px-6 lg:px-8" style={{ background: '#0F172A' }}>
          <div className="mx-auto max-w-7xl">
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(201,169,97,0.4))' }} />
              <span className="font-body text-xs font-semibold tracking-widest uppercase px-4" style={{ color: 'rgba(201,169,97,0.6)' }}>Enterprise Platform Pricing</span>
              <div className="flex-1 h-px" style={{ background: 'linear-gradient(to left, transparent, rgba(201,169,97,0.4))' }} />
            </div>
          </div>
        </div>

        {/* ===== PRICING SECTION ===== */}
        {/* Labeled as "Enterprise Platform Pricing" */}
        <section id="pricing" className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(180deg, #0F172A 0%, #0a1628 100%)', paddingTop: '80px', paddingBottom: '80px' }}>
          <div className="section-divider mb-16" />
          <div className="mx-auto max-w-7xl">
            <div className="mb-16 text-center animate-on-scroll">
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4 border" style={{ backgroundColor: 'rgba(201,169,97,0.1)', borderColor: 'rgba(201,169,97,0.3)' }}>
                <span className="font-body text-xs font-semibold tracking-widest uppercase" style={{ color: '#C9A961' }}>Enterprise Platform</span>
              </div>
              <h2
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
                style={{ letterSpacing: '-0.025em', marginBottom: '1.5rem' }}
              >
                Enterprise Platform Pricing
              </h2>
              <p className="mx-auto mt-5 max-w-2xl font-body text-base lg:text-lg" style={{ color: 'rgba(255,255,255,0.65)' }}>For organizations managing AI systems — flexible plans designed for organizations of all sizes</p>
              
              {/* Annual/Monthly Toggle */}
              <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-white/20 bg-white/5 p-1">
                <button
                  onClick={() => setIsAnnual(false)}
                  className={`rounded-full px-5 py-2 text-sm font-medium transition-all ${
                    !isAnnual ? 'bg-[#C9A961] text-[#0F172A] font-bold' : 'text-white/60 hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setIsAnnual(true)}
                  className={`rounded-full px-5 py-2 text-sm font-medium transition-all flex items-center gap-2 ${
                    isAnnual ? 'bg-[#C9A961] text-[#0F172A] font-bold' : 'text-white/60 hover:text-white'
                  }`}
                >
                  Annual
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                    isAnnual ? 'bg-[#0F172A]/30 text-[#0F172A]' : 'bg-green-500/20 text-green-400'
                  }`}>Save 20%</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Basic */}
              <div className="card-hover rounded-xl border p-8 animate-on-scroll" style={{ background: 'linear-gradient(135deg, rgba(30,58,95,0.3) 0%, rgba(15,23,42,0.8) 100%)', borderColor: 'rgba(255,255,255,0.1)', transitionDelay: '0.1s' }}>
                <div className="mb-5"><span className="inline-block rounded-full px-3 py-1 font-body text-xs font-semibold" style={{ backgroundColor: 'rgba(59,130,246,0.2)', color: '#60a5fa' }}>Basic</span></div>
                <div className="mb-6">
                  <div className="flex items-baseline"><span className="font-heading text-5xl font-bold text-white">Free</span></div>
                  <p className="mt-2 font-body text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>Perfect for getting started</p>
                </div>
                <ul className="mb-8 space-y-3">
                  {['Up to 5 AI use cases', 'Basic risk assessments', 'CMRI scoring', '3 team members', 'Email support'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <Icon name="CheckCircleIcon" size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#C9A961' }} />
                      <span className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link href="/login?mode=signup" className="block w-full rounded-lg border-2 py-3 px-6 min-h-12 flex items-center justify-center text-center font-body text-sm font-semibold transition-all duration-300 hover:scale-105" style={{ borderColor: 'rgba(201,169,97,0.5)', color: '#C9A961', backgroundColor: 'transparent' }} onClick={() => handlePricingClick('Basic')}>Start Free Trial</Link>
              </div>

              {/* Professional */}
              <div className="card-hover relative rounded-xl border-2 p-8 animate-on-scroll" style={{ background: 'linear-gradient(135deg, rgba(30,58,95,0.6) 0%, rgba(15,23,42,0.9) 100%)', borderColor: '#C9A961', transitionDelay: '0.2s', boxShadow: '0 0 40px rgba(201,169,97,0.15)' }}>
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="rounded-full px-4 py-1 font-body text-xs font-semibold" style={{ backgroundColor: '#C9A961', color: '#0F172A' }}>Most Popular</span>
                </div>
                <div className="mb-5"><span className="inline-block rounded-full px-3 py-1 font-body text-xs font-semibold" style={{ backgroundColor: 'rgba(34,197,94,0.2)', color: '#4ade80' }}>Professional</span></div>
                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="font-heading text-5xl font-bold text-white">{isAnnual ? '$239' : '$299'}</span>
                    <span className="ml-2 font-body text-base" style={{ color: 'rgba(255,255,255,0.5)' }}>/month</span>
                  </div>
                  <p className="mt-2 font-body text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>{isAnnual ? 'Billed annually ($2,868/yr) — save 20%' : 'or $2,868/year (save 20%)'}</p>
                </div>
                <ul className="mb-8 space-y-3">
                  {['Everything in Basic, plus:', 'Unlimited AI use cases', 'Executive narratives & board reports', 'Department-level analytics', 'GDPR & NIST certification tracking', '10 team members', 'Priority email support'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <Icon name="CheckCircleIcon" size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#C9A961' }} />
                      <span className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link href="/login?mode=signup" className="block w-full rounded-lg py-3 text-center font-body text-sm font-bold transition-all hover:scale-105" style={{ backgroundColor: '#C9A961', color: '#0F172A' }}>
                  Start Free Trial
                </Link>
              </div>

              {/* Enterprise */}
              <div className="card-hover rounded-xl border p-8 animate-on-scroll" style={{ background: 'linear-gradient(135deg, rgba(30,58,95,0.3) 0%, rgba(15,23,42,0.8) 100%)', borderColor: 'rgba(168,85,247,0.3)', transitionDelay: '0.3s' }}>
                <div className="mb-5"><span className="inline-block rounded-full px-3 py-1 font-body text-xs font-semibold" style={{ backgroundColor: 'rgba(168,85,247,0.2)', color: '#c084fc' }}>Enterprise</span></div>
                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="font-heading text-5xl font-bold text-white">{isAnnual ? '$639' : '$799'}</span>
                    <span className="ml-2 font-body text-base" style={{ color: 'rgba(255,255,255,0.5)' }}>/month</span>
                  </div>
                  <p className="mt-2 font-body text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>{isAnnual ? 'Billed annually ($7,668/yr) — save 20%' : 'or $7,668/year (save 20%)'}</p>
                </div>
                <ul className="mb-8 space-y-3">
                  {['Everything in Professional, plus:', 'Predictive risk trend modeling', 'ISO 42001, SOC 2, EU AI Act certifications', 'Governance maturity modeling', 'Risk appetite configuration', 'Unlimited team members', 'Dedicated account manager', '24/7 priority support'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <Icon name="CheckCircleIcon" size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#C9A961' }} />
                      <span className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="space-y-3">
                  <Link href="/login?mode=signup" className="block w-full rounded-lg border-2 py-3 px-6 min-h-12 flex items-center justify-center text-center font-body text-sm font-semibold transition-all duration-300 hover:scale-105" style={{ borderColor: 'rgba(201,169,97,0.5)', color: '#C9A961', backgroundColor: 'transparent' }} onClick={() => handlePricingClick('Enterprise')}>Start Free Trial</Link>
                  <button onClick={() => setShowDemoModal(true)} className="block w-full rounded-lg py-3 px-6 min-h-12 flex items-center justify-center text-center font-body text-sm font-medium transition-all duration-300 hover:bg-white/10" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'transparent' }}>Book a Demo</button>
                </div>
              </div>
            </div>

            <div className="mt-10 text-center">
              <p className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
                Need a custom plan?{' '}
                <a href="mailto:sales@chronomind.ai" className="font-medium hover:underline" style={{ color: '#C9A961' }}>Contact our sales team</a>
              </p>
            </div>
          </div>
          <div className="section-divider mt-16" />
        </section>

        {/* ===== INDUSTRIES SECTION ===== */}
        <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #0F172A 100%)', paddingTop: '80px', paddingBottom: '80px' }}>
          <div className="mx-auto max-w-7xl">
            <div className="mb-16 text-center animate-on-scroll">
              <h2
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
                style={{ letterSpacing: '-0.025em', marginBottom: '1.5rem' }}
              >
                Trusted Across Industries
              </h2>
              <p className="mx-auto mt-5 max-w-2xl font-body text-base lg:text-lg" style={{ color: 'rgba(255,255,255,0.65)' }}>Leading organizations rely on ChronoMind™ for AI governance</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'CurrencyDollarIcon', title: 'Financial Services', desc: 'Risk management for AI-powered trading algorithms, fraud detection, and credit scoring systems.' },
                { icon: 'HeartIcon', title: 'Healthcare', desc: 'HIPAA-compliant governance for diagnostic AI, patient data analytics, and clinical decision support.' },
                { icon: 'CpuChipIcon', title: 'Technology', desc: 'Governance frameworks for AI product development, model deployment, and algorithmic transparency.' },
                { icon: 'BuildingLibraryIcon', title: 'Government', desc: 'Public sector AI governance aligned with federal regulations, transparency requirements, and accountability standards.' },
              ].map((industry, i) => (
                <div key={industry.title} className="card-hover rounded-xl border p-6 text-center animate-on-scroll" style={{ background: 'linear-gradient(135deg, rgba(30,58,95,0.3) 0%, rgba(15,23,42,0.7) 100%)', borderColor: 'rgba(201,169,97,0.15)', transitionDelay: `${0.1 * (i + 1)}s` }}>
                  {/* 48px bold SVG icon with gold color and hover animation */}
                  <div className="mb-4 flex justify-center">
                    <div
                      className="industry-icon-wrap flex h-16 w-16 items-center justify-center rounded-full"
                      style={{ backgroundColor: 'rgba(201,169,97,0.15)' }}
                    >
                      <Icon name={industry.icon as any} size={32} style={{ color: '#C9A961' }} />
                    </div>
                  </div>
                  <h3 className="mb-2 font-heading text-lg font-bold text-white">{industry.title}</h3>
                  <p className="font-body text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>{industry.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== ROI CALCULATOR SECTION ===== */}
        <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #0F172A 100%)', paddingTop: '80px', paddingBottom: '80px' }}>
          <div className="mx-auto max-w-5xl">
            <div className="mb-12 text-center animate-on-scroll">
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4 border" style={{ backgroundColor: 'rgba(201,169,97,0.1)', borderColor: 'rgba(201,169,97,0.3)' }}>
                <span className="font-body text-xs font-semibold tracking-widest uppercase" style={{ color: '#C9A961' }}>ROI Calculator</span>
              </div>
              <h2
                className="font-heading text-3xl sm:text-4xl font-bold text-white"
                style={{ letterSpacing: '-0.025em', marginBottom: '1.5rem' }}
              >
                What Does Non-Compliance Cost You?
              </h2>
              <p className="mx-auto mt-4 max-w-2xl font-body text-base" style={{ color: 'rgba(255,255,255,0.65)' }}>EU AI Act fines can reach up to 6% of global annual revenue. See how ChronoMind™ protects your bottom line.</p>
            </div>

            <div className="rounded-2xl border p-8" style={{ background: 'linear-gradient(135deg, rgba(30,58,95,0.4) 0%, rgba(15,23,42,0.9) 100%)', borderColor: 'rgba(201,169,97,0.2)' }}>
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                {/* Sliders */}
                <div className="space-y-8">
                  {/* Revenue Slider */}
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <label className="font-body text-sm font-medium text-white">Annual Revenue</label>
                      <div className="relative group">
                        <span
                          className="roi-value-display font-heading text-xl font-bold"
                          style={{ color: '#C9A961' }}
                        >
                          ${revenueDisplay}M
                        </span>
                        {/* Tooltip */}
                        <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-10">
                          <div className="rounded-lg px-3 py-1.5 text-xs font-medium whitespace-nowrap" style={{ backgroundColor: '#C9A961', color: '#0F172A' }}>
                            ${revenueDisplay}M annual revenue
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        type="range"
                        min="1"
                        max="500"
                        value={revenue}
                        onChange={(e) => setRevenue(Number(e.target.value))}
                        onFocus={() => setActiveSlider('revenue')}
                        onBlur={() => setActiveSlider(null)}
                        className={`roi-slider ${activeSlider === 'revenue' ? 'active-slider' : ''}`}
                        style={{
                          background: `linear-gradient(to right, #C9A961 ${(revenue / 500) * 100}%, rgba(255,255,255,0.15) ${(revenue / 500) * 100}%)`,
                        }}
                      />
                    </div>
                    <div className="mt-1 flex justify-between text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      <span>$1M</span><span>$500M</span>
                    </div>
                  </div>

                  {/* AI Systems Slider */}
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <label className="font-body text-sm font-medium text-white">Number of AI Systems</label>
                      <div className="relative group">
                        <span
                          className="roi-value-display font-heading text-xl font-bold"
                          style={{ color: '#C9A961' }}
                        >
                          {aiSystemsDisplay}
                        </span>
                        {/* Tooltip */}
                        <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block z-10">
                          <div className="rounded-lg px-3 py-1.5 text-xs font-medium whitespace-nowrap" style={{ backgroundColor: '#C9A961', color: '#0F172A' }}>
                            {aiSystemsDisplay} AI systems
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        type="range"
                        min="1"
                        max="50"
                        value={aiSystems}
                        onChange={(e) => setAiSystems(Number(e.target.value))}
                        onFocus={() => setActiveSlider('aiSystems')}
                        onBlur={() => setActiveSlider(null)}
                        className={`roi-slider ${activeSlider === 'aiSystems' ? 'active-slider' : ''}`}
                        style={{
                          background: `linear-gradient(to right, #C9A961 ${(aiSystems / 50) * 100}%, rgba(255,255,255,0.15) ${(aiSystems / 50) * 100}%)`,
                        }}
                      />
                    </div>
                    <div className="mt-1 flex justify-between text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      <span>1</span><span>50</span>
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="space-y-4">
                  <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-red-400 mb-1">Potential EU AI Act Fine</p>
                    <p className="roi-value-display font-heading text-3xl font-bold text-red-400">${potentialFine.toLocaleString()}M</p>
                    <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Up to 6% of ${revenueDisplay}M revenue</p>
                  </div>

                  <div className="rounded-xl border border-[#C9A961]/30 bg-[#C9A961]/10 p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#C9A961' }}>ChronoMind™ Enterprise Cost</p>
                    <p className="roi-value-display font-heading text-3xl font-bold" style={{ color: '#C9A961' }}>${(platformCost / 1000).toFixed(1)}K/yr</p>
                    <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>{isAnnual ? 'Annual billing (20% off)' : 'Monthly billing'}</p>
                  </div>

                  <div className="rounded-xl border border-green-500/30 bg-green-500/10 p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-green-400 mb-1">Potential Savings</p>
                    <p className="roi-value-display font-heading text-3xl font-bold text-green-400">${(savings / 1000000).toFixed(1)}M+</p>
                    <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Fine avoided minus platform cost</p>
                  </div>

                  <Link href="/login?mode=signup" className="block w-full rounded-lg py-3 text-center font-body text-sm font-bold transition-all hover:scale-105" style={{ backgroundColor: '#C9A961', color: '#0F172A' }}>
                    Start Free Trial
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== TESTIMONIALS SECTION ===== */}
        <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(180deg, #0F172A 0%, #0a1628 100%)', paddingTop: '80px', paddingBottom: '80px' }}>
          <div className="mx-auto max-w-7xl">
            <div className="mb-16 text-center animate-on-scroll">
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4 border" style={{ backgroundColor: 'rgba(201,169,97,0.1)', borderColor: 'rgba(201,169,97,0.3)' }}>
                <span className="font-body text-xs font-semibold tracking-widest uppercase" style={{ color: '#C9A961' }}>Customer Stories</span>
              </div>
              <h2
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
                style={{ letterSpacing: '-0.025em', marginBottom: '1.5rem' }}
              >
                What Our Customers Say
              </h2>
              <p className="mx-auto mt-5 max-w-2xl font-body text-base lg:text-lg" style={{ color: 'rgba(255,255,255,0.65)' }}>Governance leaders trust ChronoMind™ to manage their most critical AI risks</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {[
                {
                  quote: "ChronoMind™ transformed how we approach AI governance. Our ISO 42001 audit preparation time dropped from 6 months to 3 weeks. The CMRI scoring gives our board the clarity they need.",
                  name: "Sarah Chen",
                  title: "Chief AI Officer",
                  company: "Global Financial Institution",
                  rating: 5,
                  delay: '0.1s',
                },
                {
                  quote: "The EU AI Act compliance wizard is exceptional. We mapped all 47 of our AI systems to regulatory requirements in days, not months. The predictive risk alerts have already prevented two compliance incidents.",
                  name: "Dr. Marcus Weber",
                  title: "Head of AI Risk",
                  company: "European Healthcare Network",
                  rating: 5,
                  delay: '0.2s',
                },
                {
                  quote: "As a federal agency, our governance requirements are uniquely demanding. ChronoMind™ is the only platform that handles our multi-jurisdiction compliance needs with the audit trail depth we require.",
                  name: "James Thornton",
                  title: "Director of Digital Transformation",
                  company: "Federal Technology Agency",
                  rating: 5,
                  delay: '0.3s',
                },
              ].map((testimonial) => (
                <div
                  key={testimonial.name}
                  className="card-hover rounded-2xl border p-7 animate-on-scroll flex flex-col"
                  style={{
                    background: 'linear-gradient(135deg, rgba(30,58,95,0.4) 0%, rgba(15,23,42,0.85) 100%)',
                    borderColor: 'rgba(201,169,97,0.15)',
                    transitionDelay: testimonial.delay,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <div className="mb-4 flex gap-1">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <svg key={i} className="h-4 w-4" fill="#C9A961" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="flex-1 font-body text-sm leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.75)' }}>
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full font-heading text-sm font-bold" style={{ backgroundColor: 'rgba(201,169,97,0.2)', color: '#C9A961' }}>
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-body text-sm font-semibold text-white">{testimonial.name}</p>
                      <p className="font-body text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{testimonial.title} · {testimonial.company}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== CTA SECTION ===== */}
        <section className="relative overflow-hidden py-20 lg:py-28 px-4 sm:px-6 lg:px-8" style={{ paddingTop: '80px', paddingBottom: '80px' }}>
          <div className="absolute inset-0 z-0">
            <Image
              src="/assets/images/ChatGPT_Image_Feb_27_2026_at_09_29_58_AM-1772204341453.png"
              alt="Dark particle background for call to action section"
              fill
              className="object-cover opacity-40"
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(30,58,95,0.95) 0%, rgba(15,23,42,0.98) 100%)' }} />
          </div>
          <div className="relative z-10 mx-auto max-w-4xl text-center">
            <h2
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white animate-on-scroll"
              style={{ letterSpacing: '-0.025em', marginBottom: '1.5rem' }}
            >
              Ready to Transform Your AI Governance?
            </h2>
            <p className="mx-auto mt-6 max-w-2xl font-body text-base lg:text-lg animate-on-scroll" style={{ color: 'rgba(255,255,255,0.75)', transitionDelay: '0.1s' }}>
              Join leading organizations in establishing enterprise-grade AI risk management and compliance frameworks.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center animate-on-scroll" style={{ transitionDelay: '0.2s' }}>
              <Link href="/login?mode=signup" className="rounded-lg py-4 px-10 min-h-14 flex items-center justify-center font-body text-lg font-semibold transition-all duration-300 hover:scale-105" style={{ backgroundColor: '#C9A961', color: '#0F172A', boxShadow: '0 4px 20px rgba(201,169,97,0.45)' }} onClick={() => handleCTAClick('bottom_cta')}>Start Free Trial</Link>
              <button onClick={() => setShowDemoModal(true)} className="rounded-lg border-2 py-4 px-10 min-h-14 flex items-center justify-center font-body text-lg font-semibold transition-all duration-300 hover:scale-105" style={{ borderColor: 'rgba(201,169,97,0.6)', color: '#C9A961', backgroundColor: 'rgba(201,169,97,0.05)' }}>Book a Demo</button>
            </div>
          </div>
        </section>

        {/* ===== FOOTER — Expanded with 4-column grid ===== */}
        <footer className="border-t py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#000000', borderColor: 'rgba(201,169,97,0.15)' }}>
          <div className="mx-auto max-w-7xl">
            {/* 4-column grid on desktop, 2-column on tablet, 1-column on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
              {/* Brand column */}
              <div>
                <AppLogo src="/assets/images/Untitled_design-28-1772205376571.png" size={64} className="mb-4" />
                <p className="font-body text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Executive AI Governance Command Center</p>
                <div className="flex gap-3">
                  <a href="mailto:sales@chronomind.ai" className="font-body text-xs px-3 py-1.5 rounded-md border transition-colors hover:border-yellow-400 hover:text-yellow-400" style={{ borderColor: 'rgba(201,169,97,0.3)', color: 'rgba(255,255,255,0.5)' }}>Contact Sales</a>
                </div>
              </div>

              {/* Platform column */}
              <div>
                <h4 className="mb-4 font-heading text-sm font-bold" style={{ color: '#C9A961' }}>Platform</h4>
                <ul className="space-y-2.5">
                  {[
                    { label: 'Features', href: '#features' },
                    { label: 'Framework', href: '#framework' },
                    { label: 'Pricing', href: '#pricing' },
                    { label: 'Certifications', href: '#certifications' },
                    { label: 'API Docs', href: '/api-reference-documentation' },
                    { label: 'Case Studies', href: '/case-studies' },
                    { label: 'Templates Library', href: '/templates-library' },
                  ].map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className="font-body text-sm transition-colors hover:text-yellow-400" style={{ color: 'rgba(255,255,255,0.5)' }} onClick={() => handleNavigationClick(`footer_${link.label.toLowerCase()}`)}>{ link.label}</a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Company column */}
              <div>
                <h4 className="mb-4 font-heading text-sm font-bold" style={{ color: '#C9A961' }}>Company</h4>
                <ul className="space-y-2.5">
                  {[
                    { label: 'About', href: 'mailto:info@chronomind.ai' },
                    { label: 'Security', href: '/privacy-policy' },
                    { label: 'Compliance', href: '/iso42001-wizard' },
                    { label: 'Blog', href: '/blog-content-management-system' },
                    { label: 'Changelog', href: '/sla-public-status-page' },
                    { label: 'Partner Program', href: '/partner-referral-program-dashboard' },
                  ].map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className="font-body text-sm transition-colors hover:text-yellow-400" style={{ color: 'rgba(255,255,255,0.5)' }}>{ link.label}</a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Support + Legal column */}
              <div>
                <h4 className="mb-4 font-heading text-sm font-bold" style={{ color: '#C9A961' }}>Support</h4>
                <ul className="space-y-2.5 mb-6">
                  {[
                    { label: 'Contact', href: 'mailto:support@chronomind.ai' },
                    { label: 'Help Center', href: '/support' },
                    { label: 'Status', href: '/sla-public-status-page' },
                    { label: 'Referral Program', href: '/partner-referral-program-dashboard' },
                  ].map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className="font-body text-sm transition-colors hover:text-yellow-400" style={{ color: 'rgba(255,255,255,0.5)' }}>{ link.label}</a>
                    </li>
                  ))}
                </ul>
                <h4 className="mb-3 font-heading text-sm font-bold" style={{ color: '#C9A961' }}>Legal</h4>
                <ul className="space-y-2">
                  <li><Link href="/terms-of-use" className="font-body text-sm transition-colors hover:text-yellow-400" style={{ color: 'rgba(255,255,255,0.5)' }}>Terms of Use</Link></li>
                  <li><Link href="/terms-of-service" className="font-body text-sm transition-colors hover:text-yellow-400" style={{ color: 'rgba(255,255,255,0.5)' }}>Terms of Service</Link></li>
                  <li><Link href="/privacy-policy" className="font-body text-sm transition-colors hover:text-yellow-400" style={{ color: 'rgba(255,255,255,0.5)' }}>Privacy Policy</Link></li>
                </ul>
              </div>
            </div>

            <div className="border-t pt-8 text-center" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              <p className="font-body text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>© 2026 ChronoMind™. All rights reserved. Enterprise AI Governance Command Center.</p>
            </div>
          </div>
        </footer>
      </div>
      <BookDemoModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} />
    </>
  );
}
